import { applyLandingMetadata } from './landing/landingSeo';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Home, ArrowLeft, Cookie } from 'lucide-react';
import { Button } from '../components/ui/button';
import MidLogo from '../components/MidLogo';

export default function CookiesPage() {
  useEffect(() => applyLandingMetadata('/cookies', false), []);

  const lastUpdate = new Date().toLocaleDateString('pt-PT', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="min-h-screen bg-gray-50 public-legal">
      {/* Header */}
      <header className="bg-[var(--brand-paper,#fff)] border-b border-gray-100 sticky top-0 z-50 public-header">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <MidLogo variant="light" size="lg" />
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-[var(--brand-surface,#fff)] rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-10 public-card">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Cookie className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Política de Cookies</h1>
              <p className="text-sm text-muted-foreground">Última atualização: {lastUpdate}</p>
            </div>
          </div>

          <div className="prose prose-gray max-w-none">
            <p className="text-lg text-muted-foreground mb-8">
              A plataforma miD utiliza cookies para melhorar a experiência de utilização e garantir o funcionamento adequado dos serviços.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4">O que são Cookies?</h2>
            <p>
              Cookies são pequenos ficheiros de texto armazenados no dispositivo do utilizador (computador, smartphone ou tablet) quando visita um website. Os cookies permitem que o website reconheça o dispositivo e memorize determinadas informações sobre a visita.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4">Tipos de Cookies Utilizados</h2>
            
            <div className="space-y-4 my-6">
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-[var(--mid-secondary)] rounded-full"></div>
                  <h3 className="font-bold">Cookies Essenciais</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Necessários para o funcionamento básico da plataforma. Incluem cookies de sessão, autenticação e segurança. Não podem ser desativados.
                </p>
                <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                  <strong>Exemplos:</strong> Sessão de utilizador, token de autenticação, preferências de idioma
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <h3 className="font-bold">Cookies Funcionais</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Permitem melhorar a funcionalidade e personalização da plataforma, como memorizar preferências do utilizador.
                </p>
                <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                  <strong>Exemplos:</strong> Preferências de visualização, filtros guardados, última página visitada
                </div>
              </div>

              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-3 h-3 bg-amber-500 rounded-full"></div>
                  <h3 className="font-bold">Cookies Analíticos</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Só são ativados se aceitares. Usamos o PostHog, com servidores na União Europeia, para perceber como usas o miD: páginas visitadas, cliques e gravações de sessão. Nas gravações, tudo o que escreves e a conversa do Buyer DNA ficam ocultos, e os teus dados financeiros (rendimento, poupança, pedido de crédito) nunca são gravados.
                </p>
                <div className="mt-2 text-xs bg-gray-50 p-2 rounded">
                  <strong>Exemplos:</strong> Páginas visitadas, tempo de permanência, origem do tráfego, gravação de sessão com campos ocultos
                </div>
              </div>
            </div>

            <h2 className="text-xl font-bold mt-8 mb-4">Gestão de Cookies</h2>
            <p>
              Na primeira visita à plataforma, será apresentado um banner que permite ao utilizador:
            </p>
            <ul className="list-disc pl-6 space-y-2 my-4">
              <li><strong>Aceitar todos os cookies:</strong> permite a utilização de todos os tipos de cookies</li>
              <li><strong>Rejeitar cookies não essenciais:</strong> apenas os cookies essenciais serão utilizados</li>
              <li><strong>Configurar:</strong> escolher individualmente quais tipos de cookies autoriza</li>
            </ul>
            <p>O utilizador pode alterar as suas preferências de cookies a qualquer momento através das definições da plataforma ou do seu browser.</p>

            <h2 className="text-xl font-bold mt-8 mb-4">Cookies de Terceiros</h2>
            <p>
              A plataforma pode utilizar serviços de terceiros que também utilizam cookies:
            </p>
            <ul className="list-disc pl-6 space-y-2 my-4">
              <li><strong>Stripe:</strong> para processamento de pagamentos</li>
              <li><strong>PostHog (União Europeia):</strong> estatísticas de utilização e gravações de sessão, só depois de aceitares os cookies analíticos</li>
            </ul>
            <p>Estes terceiros têm as suas próprias políticas de privacidade e cookies.</p>

            <h2 className="text-xl font-bold mt-8 mb-4">Como Desativar Cookies no Browser</h2>
            <p>
              Além das opções disponíveis na plataforma, pode gerir cookies através das definições do seu browser:
            </p>
            <ul className="list-disc pl-6 space-y-2 my-4">
              <li><strong>Chrome:</strong> Definições &gt; Privacidade e segurança &gt; Cookies</li>
              <li><strong>Firefox:</strong> Definições &gt; Privacidade e Segurança &gt; Cookies</li>
              <li><strong>Safari:</strong> Preferências &gt; Privacidade &gt; Cookies</li>
              <li><strong>Edge:</strong> Definições &gt; Cookies e permissões do site</li>
            </ul>
            <p className="mt-4 text-amber-600 bg-amber-50 p-4 rounded-lg legal-note">
              <strong>Nota:</strong> A desativação de cookies essenciais pode afetar o funcionamento correto da plataforma.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4">Período de Conservação</h2>
            <p>Os cookies têm diferentes períodos de conservação:</p>
            <ul className="list-disc pl-6 space-y-2 my-4">
              <li><strong>Cookies de sessão:</strong> eliminados quando fecha o browser</li>
              <li><strong>Cookies persistentes:</strong> conservados até à data de expiração definida ou até serem eliminados manualmente</li>
            </ul>

            <h2 className="text-xl font-bold mt-8 mb-4">Alterações à Política</h2>
            <p>
              Esta política de cookies pode ser atualizada periodicamente. Recomendamos a consulta regular desta página.
            </p>

            <h2 className="text-xl font-bold mt-8 mb-4">Contacto</h2>
            <p>Para questões sobre a utilização de cookies, contacte-nos através da plataforma.</p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[var(--brand-paper,#fff)] border-t border-gray-100 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} miD. Todos os direitos reservados.</p>
          <div className="flex justify-center gap-6 mt-4">
            <Link to="/terms" className="hover:text-primary">Termos e Condições</Link>
            <Link to="/privacy" className="hover:text-primary">Política de Privacidade</Link>
            <Link to="/cookies" className="hover:text-primary">Política de Cookies</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
